import React, { createContext, forwardRef, useContext, useMemo } from 'react'
import { Pressable, type StyleProp, Text, View, type ViewStyle } from 'react-native'
import Svg, { Path } from 'react-native-svg'
import { twMerge } from 'tailwind-merge'

import { FOCUS_RING_CLASS } from '../../focusRing'
import { GLYPH_COLORS, MinusGlyph } from '../primitive-glyphs/glyphs'

type CheckboxSize = 'sm' | 'md' | 'lg'

const Checkmark: React.FC<{ size: number }> = ({ size }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path
            d='M20 6L9 17l-5-5'
            stroke={GLYPH_COLORS.onPrimary}
            strokeWidth={3}
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </Svg>
)

type CheckboxItemContextValue = {
    checked: boolean
    indeterminate: boolean
    invalid: boolean
    size: CheckboxSize
}
const CheckboxItemContext = createContext<CheckboxItemContextValue>({
    checked: false,
    indeterminate: false,
    invalid: false,
    size: 'md',
})

// Controlled group (API parity). Standalone is the common case; each child reads
// its checked state / change handler from here when it declares a `value`.
type CheckboxGroupContextValue = {
    values: string[]
    toggle: (value: string, next: boolean) => void
    isDisabled?: boolean
}
const CheckboxGroupContext = createContext<CheckboxGroupContextValue | null>(null)

export type CheckboxGroupProps = {
    value?: string[]
    onChange?: (values: string[]) => void
    isDisabled?: boolean
    className?: string
    children?: React.ReactNode
}
export const CheckboxGroup = forwardRef<React.ComponentRef<typeof View>, CheckboxGroupProps>(
    ({ value = [], onChange, isDisabled, className, children }, ref) => {
        const toggle = React.useCallback(
            (itemValue: string, next: boolean) => {
                const has = value.includes(itemValue)
                if (next && !has) {
                    onChange?.([...value, itemValue])
                } else if (!next && has) {
                    onChange?.(value.filter(v => v !== itemValue))
                }
            },
            [value, onChange],
        )
        const ctx = useMemo(
            () => ({ values: value, toggle, isDisabled }),
            [value, toggle, isDisabled],
        )
        return (
            <CheckboxGroupContext.Provider value={ctx}>
                <View ref={ref} role='group' className={twMerge('gap-3', className)}>
                    {children}
                </View>
            </CheckboxGroupContext.Provider>
        )
    },
)
CheckboxGroup.displayName = 'CheckboxGroup'

export type CheckboxProps = {
    value?: string
    isChecked?: boolean
    isIndeterminate?: boolean
    isDisabled?: boolean
    isInvalid?: boolean
    size?: CheckboxSize
    onChange?: (checked: boolean) => void
    className?: string
    /** Native-only escape hatch for call sites whose padding is still an RN style. */
    style?: StyleProp<ViewStyle>
    testID?: string
    children?: React.ReactNode
}
export const Checkbox = forwardRef<React.ComponentRef<typeof Pressable>, CheckboxProps>(
    (
        {
            value,
            isChecked,
            isIndeterminate,
            isDisabled,
            isInvalid,
            size = 'md',
            onChange,
            className,
            style,
            testID,
            children,
        },
        ref,
    ) => {
        const group = useContext(CheckboxGroupContext)
        const inGroup = group !== null && typeof value === 'string'
        const checked = inGroup ? group.values.includes(value) : !!isChecked
        const disabled = (inGroup ? group.isDisabled : isDisabled) ?? false
        const toggle = () => {
            if (disabled) {
                return
            }
            if (inGroup) {
                group.toggle(value, !checked)
            } else {
                onChange?.(!checked)
            }
        }
        const itemCtx = useMemo(
            () => ({ checked, indeterminate: !!isIndeterminate, invalid: !!isInvalid, size }),
            [checked, isIndeterminate, isInvalid, size],
        )
        return (
            <Pressable
                ref={ref}
                testID={testID}
                style={style}
                role='checkbox'
                accessibilityRole='checkbox'
                accessibilityState={{ checked, disabled }}
                // react-native-web drops `accessibilityState` on Pressable, so the
                // state never reached the DOM — same explicit prop as Switch.
                aria-checked={isIndeterminate ? 'mixed' : checked}
                disabled={disabled}
                onPress={toggle}
                className={twMerge(
                    'group/checkbox flex-row items-center web:cursor-pointer',
                    FOCUS_RING_CLASS,
                    size === 'sm' ? 'gap-2' : 'gap-3',
                    disabled && 'opacity-40 pointer-events-none web:cursor-not-allowed',
                    className,
                )}
            >
                <CheckboxItemContext.Provider value={itemCtx}>
                    {React.Children.count(children) > 0 ? children : <CheckboxIndicator />}
                </CheckboxItemContext.Provider>
            </Pressable>
        )
    },
)
Checkbox.displayName = 'Checkbox'

const boxSize: Record<CheckboxSize, string> = {
    sm: 'h-4 w-4 border-2',
    md: 'h-5 w-5 border-2',
    lg: 'h-6 w-6 border-[3px]',
}
const tickSize: Record<CheckboxSize, number> = { sm: 12, md: 16, lg: 20 }

export type CheckboxIndicatorProps = { className?: string }
export const CheckboxIndicator = forwardRef<
    React.ComponentRef<typeof View>,
    CheckboxIndicatorProps
>(({ className }, ref) => {
    const { checked, indeterminate, invalid, size } = useContext(CheckboxItemContext)
    const filled = checked || indeterminate
    return (
        <View
            ref={ref}
            className={twMerge(
                'shrink-0 items-center justify-center rounded-[3px] border-solid',
                boxSize[size],
                filled
                    ? 'border-action-default bg-action-default group-hover/checkbox:border-action-hover group-hover/checkbox:bg-action-hover group-active/checkbox:border-primary-pressed group-active/checkbox:bg-primary-pressed'
                    : 'border-action-grey-action group-hover/checkbox:border-action-grey-action-hover',
                invalid && 'border-status-critical',
                className,
            )}
        >
            {indeterminate ? (
                <MinusGlyph size={tickSize[size]} color={GLYPH_COLORS.onPrimary} />
            ) : checked ? (
                <Checkmark size={tickSize[size]} />
            ) : null}
        </View>
    )
})
CheckboxIndicator.displayName = 'CheckboxIndicator'

export type CheckboxLabelProps = { className?: string; children?: React.ReactNode }
export const CheckboxLabel = forwardRef<React.ComponentRef<typeof Text>, CheckboxLabelProps>(
    ({ className, children }, ref) => (
        <Text
            ref={ref}
            className={twMerge('shrink text-content-default web:select-none', className)}
        >
            {children}
        </Text>
    ),
)
CheckboxLabel.displayName = 'CheckboxLabel'

export type CheckboxIconProps = { className?: string }
export const CheckboxIcon = forwardRef<React.ComponentRef<typeof View>, CheckboxIconProps>(
    ({ className }, ref) => {
        const { size } = useContext(CheckboxItemContext)
        return (
            <View ref={ref} className={className}>
                <Checkmark size={tickSize[size]} />
            </View>
        )
    },
)
CheckboxIcon.displayName = 'CheckboxIcon'
